import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  Alert,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const FinanceManagement = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [financeData, setFinanceData] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [stats, setStats] = useState({
    totalDue: 0,
    totalPaid: 0,
    overdue: 0,
    upcoming: 0
  });

  useEffect(() => {
    fetchFinanceData();
    // Payment history is now fetched with finance data
  }, []);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('student-portal/finance');
      if (response.data.success) {
        setFinanceData(response.data.data.fees || []);
        setPaymentHistory(response.data.data.payments || []);
        // Use backend statistics if available, otherwise calculate
        if (response.data.data.statistics) {
          setStats({
            totalDue: parseFloat(response.data.data.statistics.totalDue || 0),
            totalPaid: parseFloat(response.data.data.statistics.totalPaid || 0),
            overdue: parseInt(response.data.data.statistics.overdueCount || 0),
            upcoming: response.data.data.fees ? response.data.data.fees.filter(fee => 
              fee.payment_status === 'pending' && new Date(fee.due_date) > new Date()
            ).length : 0
          });
        } else {
          calculateStats(response.data.data.fees || []);
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch finance data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get('student-portal/finance'); // Get payments from finance endpoint
      if (response.data.success) {
        setPaymentHistory(response.data.data.payments || []);
      }
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
    }
  };

  const calculateStats = (data) => {
    const stats = data.reduce((acc, fee) => {
      const amount = parseFloat(fee.amount);
      const dueDate = new Date(fee.due_date);
      const today = new Date();
      
      if (fee.status === 'paid') {
        acc.totalPaid += amount;
      } else if (fee.status === 'pending') {
        acc.totalDue += amount;
        if (dueDate < today) {
          acc.overdue += amount;
        } else {
          acc.upcoming += amount;
        }
      }
      return acc;
    }, { totalDue: 0, totalPaid: 0, overdue: 0, upcoming: 0 });
    
    setStats(stats);
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`student-portal/pay-fee/${selectedFee.fee_id}`, {
        payment_method: paymentMethod
      });
      
      if (response.data.success) {
        enqueueSnackbar('Payment processed successfully', { variant: 'success' });
        fetchFinanceData();
        // Payment history will be updated with finance data
        setPaymentDialog(false);
        setSelectedFee(null);
      }
    } catch (error) {
      enqueueSnackbar('Payment failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const downloadReceipt = (feeId) => {
    window.open(`/api/student-portal/receipt/${feeId}`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card sx={{ 
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `1px solid ${color}30`,
        height: '100%'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: color }}>
                ${value.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="textSecondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Finance Management
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Due" 
            value={stats.totalDue} 
            icon={<PaymentIcon />} 
            color="#FF9800"
            subtitle={`${financeData.filter(f => f.status === 'pending').length} pending fees`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Paid" 
            value={stats.totalPaid} 
            icon={<ReceiptIcon />} 
            color="#4CAF50"
            subtitle={`${financeData.filter(f => f.status === 'paid').length} paid fees`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Overdue" 
            value={stats.overdue} 
            icon={<AccountBalanceIcon />} 
            color="#F44336"
            subtitle="Immediate attention required"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Upcoming" 
            value={stats.upcoming} 
            icon={<HistoryIcon />} 
            color="#2196F3"
            subtitle="Due within 30 days"
          />
        </Grid>
      </Grid>

      {/* Outstanding Fees */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Outstanding Fees</Typography>
            <Button 
              variant="contained" 
              color="primary"
              disabled={financeData.filter(f => f.status === 'pending').length === 0}
              onClick={() => {
                // Pay all pending fees logic
                enqueueSnackbar('Bulk payment feature coming soon', { variant: 'info' });
              }}
            >
              Pay All Pending
            </Button>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fee Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {financeData.filter(fee => fee.status !== 'paid').map((fee) => (
                  <TableRow key={fee.fee_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {fee.fee_type}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Academic Year {fee.academic_year || '2024-25'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        ${parseFloat(fee.amount || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(fee.due_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={fee.status} 
                        color={getStatusColor(fee.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="contained" 
                        color="primary"
                        onClick={() => {
                          setSelectedFee(fee);
                          setPaymentDialog(true);
                        }}
                      >
                        Pay Now
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {financeData.filter(fee => fee.status !== 'paid').length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Alert severity="success">
                        All fees are paid! You're up to date.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Payment History</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Fee Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map((payment) => (
                  <TableRow key={payment.payment_id}>
                    <TableCell>
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{payment.fee_type}</TableCell>
                    <TableCell>${parseFloat(payment.amount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={payment.payment_method} 
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadReceipt(payment.fee_id)}
                      >
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {paymentHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary">
                        No payment history available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Pay Fee - {selectedFee?.fee_type}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              Amount: <strong>${selectedFee?.amount}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Due Date: {selectedFee && new Date(selectedFee.due_date).toLocaleDateString()}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <TextField
            select
            fullWidth
            label="Payment Method"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="online">Online Payment</MenuItem>
            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="check">Check</MenuItem>
          </TextField>

          <Alert severity="info" sx={{ mt: 2 }}>
            This is a demo payment. In a real system, you would be redirected to a secure payment gateway.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Cancel</Button>
          <Button 
            onClick={handlePayment} 
            variant="contained"
            startIcon={<CreditCardIcon />}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Pay Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinanceManagement;
